/* Dr.Primal — AJAX cart drawer.

   Division of labour, and it is deliberate:

   - Liquid renders every price, every total, every discount title and every FREE badge.
     This file never formats money, never multiplies a price by a quantity and never
     decides whether a line is discounted. It posts to the Cart API, asks Shopify to
     re-render `sections/dp-cart-drawer.liquid`, and swaps the two regions that changed.
     The store serves EUR, GBP and USD for the same product; a number formatted here
     would be wrong for most visitors.
   - Liquid stamps ids. This file reads `data-` attributes. It never matches a line or a
     variant by title or option text.
   - It never calls `/cart/clear.js`, never applies a discount code, and never sends
     `_bundle_*` line properties.
   - Failures are shown, not swallowed. There is no blanket try/catch: a non-OK response
     or a missing section in the payload puts a visible message in the drawer and a
     console error, and any optimistic quantity is rolled back to what the server last
     confirmed.

   Contract for other components (the phase 3 buy box in particular):

   - A `form[action*="/cart/add"]` is intercepted here by default, so a plain product
     form opens the drawer with no extra wiring. A form that manages its own submission
     — because it posts several lines, or reconciles the total afterwards — must carry
     `data-dp-cart-managed` so this file leaves it alone. Two handlers on one form is a
     documented production defect in the team's other themes.
   - `document.dispatchEvent(new CustomEvent('dp:cart:open'))` opens the drawer and
     refreshes it from the server first.
   - `document.dispatchEvent(new CustomEvent('dp:cart:updated', { detail: { sections } }))`
     hands this file the `sections` payload from someone else's Cart API call; add
     `open: true` to open the drawer as well.
   - After its own updates this file dispatches `dp:cart:updated` on `document` with
     `{ cart, sections, fromDrawer: true }`. */
(function () {
  'use strict';

  var SECTIONS = 'dp-cart-drawer,dp-header';
  var QUANTITY_DEBOUNCE = 300;
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var drawer = null;
  var panel = null;
  var message = null;
  var lastFocused = null;
  var pending = Object.create(null);

  function root() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return '/';
  }

  function sectionsUrl() {
    return window.location.pathname + window.location.search;
  }

  /* Copy comes from Liquid, through `| t`, stamped on the drawer as data attributes —
     the same route `dp-buy-box.js` uses. No user-facing English is written in this file;
     the store is served in three languages' worth of markets and a string hard-coded
     here could never be translated or edited. */
  function copy(name, replacements) {
    var value = drawer ? drawer.getAttribute('data-dp-msg-' + name) || '' : '';
    Object.keys(replacements || {}).forEach(function (token) {
      value = value.replace('[' + token + ']', replacements[token]);
    });
    return value;
  }

  function attrSelector(name, value) {
    return '[' + name + '="' + String(value).replace(/["\\]/g, '\\$&') + '"]';
  }

  function focusableWithin(node) {
    return Array.prototype.filter.call(node.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0;
    });
  }

  /* ---------- visible failure ---------- */

  function clearMessage() {
    if (!message) return;
    message.hidden = true;
    message.textContent = '';
    message.classList.remove('dp-cart-drawer__message--notice');
  }

  function showMessage(text, tone) {
    if (!message) return;
    message.textContent = text;
    message.classList.toggle('dp-cart-drawer__message--notice', tone === 'notice');
    message.hidden = false;
  }

  function fail(error) {
    // A failed refresh must not leave the free-shipping bar held empty; see FREE
    // SHIPPING REACHED below.
    if (drawer) drawer.removeAttribute('data-dp-ship-wait');
    showMessage(error.message, 'error');
    console.error('[dp-cart-drawer]', error);
  }

  /* ---------- open / close ---------- */

  function isOpen() {
    return drawer.getAttribute('data-dp-open') === 'true';
  }

  function open(trigger) {
    if (isOpen()) return;
    lastFocused =
      trigger ||
      (document.activeElement && document.activeElement !== document.body
        ? document.activeElement
        : null);
    drawer.setAttribute('data-dp-open', 'true');
    drawer.removeAttribute('aria-hidden');
    drawer.removeAttribute('inert');
    document.documentElement.setAttribute('data-dp-cart-locked', 'true');
    focusFirst();
  }

  // The panel is visibility:hidden until the open attribute lands, and focus() is
  // rejected on a hidden element, so try again after the style recalc.
  function focusFirst(attempt) {
    var tries = attempt || 0;
    requestAnimationFrame(function () {
      if (!isOpen()) return;
      var targets = focusableWithin(panel);
      if (targets.length) targets[0].focus();
      if (!panel.contains(document.activeElement) && tries < 2) focusFirst(tries + 1);
    });
  }

  function close() {
    if (!isOpen()) return;
    // Move focus out before the subtree goes inert.
    if (lastFocused && document.contains(lastFocused)) {
      lastFocused.focus();
    } else {
      var trigger = document.querySelector('[data-dp-cart-open]');
      var main = document.getElementById('dp-main');
      if (trigger) trigger.focus();
      else if (main) main.focus();
    }
    drawer.setAttribute('data-dp-open', 'false');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('inert', '');
    document.documentElement.removeAttribute('data-dp-cart-locked');
    clearMessage();
  }

  function onKeydown(event) {
    if (!isOpen()) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }

    if (event.key !== 'Tab') return;

    var targets = focusableWithin(panel);
    if (!targets.length) {
      event.preventDefault();
      return;
    }

    var first = targets[0];
    var last = targets[targets.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    } else if (!panel.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---------- rendering ---------- */

  function captureFocus() {
    var active = document.activeElement;
    if (!active || !panel.contains(active)) return null;
    var holder = active.closest('[data-dp-focus-key]');
    if (!holder) return null;
    return {
      key: holder.getAttribute('data-dp-focus-key'),
      step: active.getAttribute('data-dp-qty-step'),
    };
  }

  function restoreFocus(state) {
    if (!state) return;
    var holder = panel.querySelector(attrSelector('data-dp-focus-key', state.key));
    if (!holder) {
      focusFirst();
      return;
    }
    var target = null;
    if (state.step) target = holder.querySelector(attrSelector('data-dp-qty-step', state.step));
    if (!target || target.disabled) target = focusableWithin(holder)[0];
    if (target) target.focus();
    else focusFirst();
  }

  function swapRegion(source, host, selector) {
    var next = source.querySelector(selector);
    var current = host.querySelector(selector);
    if (!next || !current) return false;
    current.innerHTML = next.innerHTML;
    return true;
  }

  function parse(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function applySections(sections) {
    if (!sections || typeof sections['dp-cart-drawer'] !== 'string') {
      throw new Error(copy('refresh'));
    }

    var focusState = captureFocus();
    var source = parse(sections['dp-cart-drawer']);
    var wasUnlocked = shippingUnlocked();
    var opening = drawer.hasAttribute('data-dp-ship-wait');
    // A run still in flight would start again on the nodes this swap creates: the bar
    // emptied and the strip shut under a second tap on the stepper. End it first.
    shippingSettled();

    if (!swapRegion(source, drawer, '[data-dp-cart-body]')) {
      throw new Error(copy('refresh'));
    }
    swapRegion(source, drawer, '[data-dp-cart-foot]');
    // The count beside the heading. The heading itself is never replaced.
    swapRegion(source, drawer, '[data-dp-cart-title-count]');
    restoreFocus(focusState);

    drawer.removeAttribute('data-dp-ship-wait');
    if (shippingUnlocked() && (opening || !wasUnlocked)) shippingReached();

    // The basket count lives in the header, which is a separate section — on the bar's
    // cart and on the open phone menu's (2026-10-13). Every region holds the same bubble,
    // so each takes the first one the fresh header carries.
    if (typeof sections['dp-header'] === 'string') {
      var count = parse(sections['dp-header']).querySelector('[data-dp-cart-count-region]');
      if (count) {
        Array.prototype.forEach.call(
          document.querySelectorAll('[data-dp-cart-count-region]'),
          function (region) {
            region.innerHTML = count.innerHTML;
          }
        );
      }
    }
  }

  function announce(cart, sections) {
    document.dispatchEvent(
      new CustomEvent('dp:cart:updated', {
        detail: { cart: cart, sections: sections, fromDrawer: true },
      })
    );
  }

  /* ---------- Cart API ---------- */

  function readJson(response) {
    return response.json().then(
      function (body) {
        if (response.ok) return body;
        var detail = body && (body.description || body.message);
        throw new Error(detail || copy('update', { status: response.status }));
      },
      function () {
        throw new Error(copy('update', { status: response.status }));
      }
    );
  }

  function postJson(path, payload) {
    return fetch(root() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    }).then(readJson);
  }

  function busy(state) {
    drawer.setAttribute('data-dp-busy', state ? 'true' : 'false');
  }

  /* The identity of a cart line, stamped by Liquid: variant id plus selling plan, never
     the line key. A key is `variantid:hash` and the hash moves — this is the drawer's
     own copy of the trap documented in `docs/offer-mechanic-lessons.md`. Reading the key
     out of one response is safe; comparing against one that was captured before a cart
     change is not, and the gift half of a Buy X Get Y rewrites its partner's key. The
     plan is part of the identity because the same variant can sit in the basket twice,
     once one-time and once on a subscription. */
  function identityOf(node) {
    var line = node && node.closest ? node.closest('[data-dp-line-key]') : null;
    var variant = line && line.getAttribute('data-dp-line-variant');
    if (!variant) return null;
    return { variant: variant, plan: line.getAttribute('data-dp-line-plan') || '' };
  }

  function planOf(item) {
    return item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
      ? String(item.selling_plan_allocation.selling_plan.id)
      : '';
  }

  function lineIn(cart, identity, key) {
    for (var i = 0; i < cart.items.length; i += 1) {
      var item = cart.items[i];
      if (identity) {
        if (String(item.variant_id) === identity.variant && planOf(item) === identity.plan) {
          return item;
        }
      } else if (item.key === key) {
        return item;
      }
    }
    return null;
  }

  function changeLine(key, quantity, rollback, identity) {
    clearMessage();
    busy(true);

    return postJson('cart/change.js', {
      id: key,
      quantity: quantity,
      sections: SECTIONS,
      sections_url: sectionsUrl(),
    })
      .then(function (cart) {
        applySections(cart.sections);
        announce(cart, cart.sections);

        if (cart.item_count === 0) {
          close();
          return;
        }

        // Shopify silently clamps a quantity it cannot fulfil. Say so rather than
        // letting the number change under the customer with no explanation.
        if (quantity > 0) {
          var line = lineIn(cart, identity, key);
          if (!line) {
            showMessage(copy('gone'), 'notice');
          } else if (line.quantity !== quantity) {
            showMessage(copy('clamped', { count: line.quantity }), 'notice');
          }
        }
      })
      .catch(function (error) {
        if (rollback) rollback();
        fail(error);
      })
      .then(function () {
        busy(false);
      });
  }

  /* The Auto-ship checkbox: ticking it upgrades, unticking it reverts.

     The offers are separate variants, not one variant with attachable plans, so a
     one-time line cannot gain a subscription in place: the swap ADDS the other variant
     (at the summed quantity of the lines it replaces) and REMOVES those lines. Order
     matters — add first, so the basket is never momentarily empty and no other guard
     fires on a zero-item cart between the two steps.

     Sequential, and only the final response's sections are applied: `cart/add.js` may
     merge into an existing line and the removes rewrite keys, so each request is issued
     against state that still exists, and the drawer ends on whatever the last call
     reports. Any failure falls back to `refreshFromServer` so the drawer always lands on
     the server's truth rather than a half-applied swap — which also puts the checkbox
     back to the state the basket really is in. */
  function toggleUpgrade(button) {
    if (drawer.getAttribute('data-dp-busy') === 'true') return;

    if (button.getAttribute('aria-checked') === 'true') {
      var otpId = parseInt(button.getAttribute('data-dp-downgrade-variant'), 10);
      if (!otpId) return;
      swapLines(button, '[data-dp-line-upgraded]', { id: otpId });
    } else {
      var variantId = parseInt(button.getAttribute('data-dp-upgrade-variant'), 10);
      var planId = parseInt(button.getAttribute('data-dp-upgrade-plan'), 10);
      if (!variantId || !planId) return;
      swapLines(button, '[data-dp-line-otp]', { id: variantId, selling_plan: planId });
    }
  }

  function swapLines(button, lineSelector, add) {
    var lines = Array.prototype.slice.call(drawer.querySelectorAll(lineSelector));
    if (!lines.length) return;

    add.quantity = lines.reduce(function (sum, line) {
      return sum + (parseInt(line.getAttribute('data-dp-line-qty-value'), 10) || 1);
    }, 0);
    add.sections = SECTIONS;
    add.sections_url = sectionsUrl();

    // The tick shows at once; the section re-render that follows is the confirmation.
    button.setAttribute('aria-checked', button.getAttribute('aria-checked') === 'true' ? 'false' : 'true');
    clearMessage();
    busy(true);

    var chain = postJson('cart/add.js', add);

    lines.forEach(function (line) {
      chain = chain.then(function () {
        return postJson('cart/change.js', {
          id: line.getAttribute('data-dp-line-key'),
          quantity: 0,
          sections: SECTIONS,
          sections_url: sectionsUrl(),
        });
      });
    });

    chain
      .then(function (cart) {
        if (cart && cart.sections) {
          applySections(cart.sections);
          announce(cart, cart.sections);
        }
      })
      .catch(function (error) {
        fail(error);
        return refreshFromServer().catch(function () {});
      })
      .then(function () {
        busy(false);
      });
  }

  function refreshFromServer() {
    var url = sectionsUrl();
    url += (url.indexOf('?') === -1 ? '?' : '&') + 'sections=' + SECTIONS;

    // Deliberately NO `Accept: application/json` header. On a product URL that header
    // wins over `?sections=` and Shopify answers with the product JSON — `{"product":
    // …}`, no sections in it at all — so every refresh from a product page failed with
    // "the basket could not be refreshed". The Section Rendering API returns JSON on the
    // strength of `?sections=` alone. This was invisible until `dp:cart:open` was
    // actually dispatched, because until then nothing ever called this function.
    return fetch(url)
      .then(readJson)
      .then(function (sections) {
        applySections(sections);
      });
  }

  /* ---------- quantity stepper ---------- */

  function inputFor(node) {
    var wrapper = node.closest('[data-dp-line-qty]');
    return wrapper ? wrapper.querySelector('[data-dp-qty-input]') : null;
  }

  /* Both step buttons reflect the value they sit either side of. `onStep` writes the
     value optimistically and greys the end it has reached; a rollback that restores only
     the number leaves a "−" button disabled at a quantity it should be usable at, and
     nothing but a reload brings it back. */
  function syncSteps(wrapper, value) {
    if (!wrapper) return;
    var min = parseInt(wrapper.getAttribute('data-dp-qty-min'), 10);
    var max = parseInt(wrapper.getAttribute('data-dp-qty-max'), 10);
    Array.prototype.forEach.call(
      wrapper.querySelectorAll('[data-dp-qty-step]'),
      function (step) {
        var delta = parseInt(step.getAttribute('data-dp-qty-step'), 10);
        step.disabled = (delta < 0 && value <= min) || (delta > 0 && value >= max);
      }
    );
  }

  function queueChange(input) {
    var line = input.closest('[data-dp-line-key]');
    if (!line) return;

    var key = line.getAttribute('data-dp-line-key');
    var identity = identityOf(input);
    var quantity = parseInt(input.value, 10);
    if (isNaN(quantity)) return;

    // `defaultValue` is the quantity Liquid rendered, i.e. the last one the server
    // confirmed. It is the only safe value to roll back to when a change fails.
    var previous = input.defaultValue;
    var wrapper = input.closest('[data-dp-qty]');

    if (pending[key]) window.clearTimeout(pending[key]);
    pending[key] = window.setTimeout(function () {
      delete pending[key];
      changeLine(
        key,
        quantity,
        function () {
          input.value = previous;
          syncSteps(wrapper, parseInt(previous, 10));
        },
        identity
      );
    }, QUANTITY_DEBOUNCE);
  }

  function onStep(button) {
    var input = inputFor(button);
    if (!input) return;

    var wrapper = button.closest('[data-dp-qty]');
    var min = parseInt(wrapper.getAttribute('data-dp-qty-min'), 10);
    var max = parseInt(wrapper.getAttribute('data-dp-qty-max'), 10);
    var delta = parseInt(button.getAttribute('data-dp-qty-step'), 10);
    var next = parseInt(input.value, 10) + delta;

    if (!isNaN(min)) next = Math.max(min, next);
    if (!isNaN(max)) next = Math.min(max, next);
    if (String(next) === input.value) return;

    input.value = next;
    // Optimistic only in the input; the price and the totals stay untouched until the
    // server has confirmed the change and re-rendered them.
    syncSteps(wrapper, next);
    queueChange(input);
  }

  /* ---------- add to cart ---------- */

  function onSubmit(event) {
    var form = event.target;
    if (!form || typeof form.matches !== 'function') return;
    if (!form.matches('form[action*="/cart/add"]')) return;
    if (form.hasAttribute('data-dp-cart-managed')) return;

    event.preventDefault();
    clearMessage();

    var submitter = event.submitter || form.querySelector('[type="submit"]');
    if (submitter) submitter.disabled = true;

    var body = new FormData(form);
    body.append('sections', SECTIONS);
    body.append('sections_url', sectionsUrl());

    fetch(root() + 'cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: body })
      .then(readJson)
      .then(function (result) {
        applySections(result.sections);
        announce(result, result.sections);
        open(submitter);
      })
      .catch(function (error) {
        fail(error);
        open(submitter);
      })
      .then(function () {
        if (submitter) submitter.disabled = false;
      });
  }

  /* ---------- wiring ---------- */

  function init() {
    drawer = document.querySelector('[data-dp-cart-drawer]');
    if (!drawer) return;

    panel = drawer.querySelector('[data-dp-cart-panel]');
    message = drawer.querySelector('[data-dp-cart-message]');
    if (!panel) return;

    drawer.setAttribute('data-dp-open', 'false');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('inert', '');

    // Delegated from the document, so a swapped-in region needs no re-binding, and
    // from an ancestor the section rendering never replaces.
    document.addEventListener('click', function (event) {
      if (!(event.target instanceof Element)) return;

      var trigger = event.target.closest('[data-dp-cart-open]');
      if (trigger) {
        event.preventDefault();
        // Dispatched rather than opened directly, so the header basket icon takes the
        // same path as everyone else's `dp:cart:open`: the drawer refreshes from the
        // server and the guard re-checks. Calling open() here meant the drawer showed
        // whatever Liquid rendered at page load — stale after a back-navigation or a
        // change made in another tab — and `refreshFromServer` was never reached at all.
        document.dispatchEvent(
          new CustomEvent('dp:cart:open', { detail: { trigger: trigger } })
        );
        return;
      }

      if (!drawer.contains(event.target)) return;

      if (event.target.closest('[data-dp-cart-close]') || event.target.closest('[data-dp-cart-backdrop]')) {
        event.preventDefault();
        close();
        return;
      }

      var upgrade = event.target.closest('[data-dp-cart-upgrade]');
      if (upgrade) {
        event.preventDefault();
        toggleUpgrade(upgrade);
        return;
      }

      var remove = event.target.closest('[data-dp-line-remove]');
      if (remove) {
        event.preventDefault();
        var line = remove.closest('[data-dp-line-key]');
        if (line) changeLine(line.getAttribute('data-dp-line-key'), 0, null);
        return;
      }

      var step = event.target.closest('[data-dp-qty-step]');
      if (step) onStep(step);
    });

    drawer.addEventListener('change', function (event) {
      if (!(event.target instanceof Element)) return;
      if (event.target.matches('[data-dp-qty-input]')) queueChange(event.target);
    });

    document.addEventListener('keydown', onKeydown);
    document.addEventListener('submit', onSubmit);

    bindNote();

    document.addEventListener('dp:cart:open', function (event) {
      if (!reducedMotion()) drawer.setAttribute('data-dp-ship-wait', '');
      open(event.detail && event.detail.trigger);
      refreshFromServer()
        .then(function () {
          // The refresh re-renders the footer, and with it a fresh, ENABLED Checkout
          // button. The guard's own `dp:cart:open` listener defers by a tick and can
          // therefore land before this swap, leaving a live button on a blocked basket.
          // This tells it to re-apply once the new footer is actually in the document.
          document.dispatchEvent(new CustomEvent('dp:cart:guard'));
        })
        .catch(fail);
    });

    document.addEventListener('dp:cart:updated', function (event) {
      var detail = event.detail;
      if (!detail || detail.fromDrawer || !detail.sections) return;
      // Not a blanket guard around cart logic: applySections has exactly one failure
      // mode — a payload without our section in it — and this turns that into the same
      // visible message every other failure produces.
      try {
        applySections(detail.sections);
      } catch (error) {
        fail(error);
      }
      if (detail.open) open(detail.trigger);
    });
  }

  /* FREE SHIPPING REACHED (client, 2026-10-10). When the basket has free shipping the
     grey bar fills with the green and the "auto-applied" strip then arrives — once, at
     the two moments it means something: when the drawer opens on such a basket, and
     when a change inside it crosses the threshold. Not on every other re-render, or a
     quantity tapped up and down would replay it each time.

     The stylesheet does the moving; this only says when. `data-dp-ship-wait` is stamped
     the instant the drawer is asked to open and holds the bar empty and the strip shut
     while the fresh markup is fetched, so what was rendered at page load cannot show
     full for a moment and then start again from nothing. `data-dp-ship-reached` is what
     the animations hang on; it comes off again afterwards, leaving the finished state,
     which is the stylesheet's ordinary one. With reduced motion neither animation is
     asked for and the bar and strip simply stand as they end. */
  var SHIP_REACHED_FOR = 2000;
  var shipTimer = 0;

  function shippingUnlocked() {
    return !!drawer.querySelector('.dp-cart-ship--unlocked');
  }

  function shippingSettled() {
    window.clearTimeout(shipTimer);
    drawer.removeAttribute('data-dp-ship-reached');
  }

  function shippingReached() {
    if (reducedMotion()) return;
    // Taken off and read back first, so a second crossing restarts a run in flight.
    shippingSettled();
    void drawer.offsetWidth;
    drawer.setAttribute('data-dp-ship-reached', '');
    shipTimer = window.setTimeout(shippingSettled, SHIP_REACHED_FOR);
  }

  /* THE NOTE (client, 2026-10-10: "make the messages slideable, and the message dots
     clickable"). The note under the strip is a scroll-snap row of two messages, so the
     sliding itself is the browser's own scrolling. This keeps the dots in step with it,
     moves it when a dot is pressed, and until the visitor has touched it moves it on
     by itself every NOTE_INTERVAL while the drawer is open — never with reduced motion.

     The note lives in the body region, which is replaced after every cart change, so
     nothing here holds a node: every handler looks the note up when it runs, and the
     scroll listener is on the drawer in the capture phase, since `scroll` does not
     bubble. A replaced note starts again at its first message. */
  var NOTE_INTERVAL = 5000;
  var noteTouched = false;

  function reducedMotion() {
    return !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function noteTrack() {
    return drawer ? drawer.querySelector('[data-dp-note-track]') : null;
  }

  function noteIndex(track) {
    return track.clientWidth ? Math.round(track.scrollLeft / track.clientWidth) : 0;
  }

  function noteSync(track) {
    var current = noteIndex(track);
    var dots = drawer.querySelectorAll('[data-dp-note-dot]');
    for (var i = 0; i < dots.length; i += 1) {
      var on = parseInt(dots[i].getAttribute('data-dp-note-dot'), 10) === current;
      dots[i].setAttribute('aria-current', on ? 'true' : 'false');
    }
  }

  function noteGo(track, index) {
    track.scrollTo({ left: index * track.clientWidth, behavior: reducedMotion() ? 'auto' : 'smooth' });
  }

  function bindNote() {
    drawer.addEventListener(
      'scroll',
      function (event) {
        var target = event.target;
        if (target instanceof Element && target.matches('[data-dp-note-track]')) noteSync(target);
      },
      true
    );

    drawer.addEventListener('click', function (event) {
      if (!(event.target instanceof Element)) return;
      var dot = event.target.closest('[data-dp-note-dot]');
      var track = noteTrack();
      if (!dot || !track) return;
      noteTouched = true;
      noteGo(track, parseInt(dot.getAttribute('data-dp-note-dot'), 10) || 0);
    });

    ['pointerdown', 'wheel', 'keydown'].forEach(function (type) {
      drawer.addEventListener(
        type,
        function (event) {
          if (event.target instanceof Element && event.target.closest('[data-dp-note]')) noteTouched = true;
        },
        { passive: true }
      );
    });

    window.setInterval(function () {
      if (noteTouched || reducedMotion() || document.hidden) return;
      if (drawer.getAttribute('data-dp-open') !== 'true') return;
      var track = noteTrack();
      if (!track || track.children.length < 2) return;
      noteGo(track, (noteIndex(track) + 1) % track.children.length);
    }, NOTE_INTERVAL);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
